import { Injectable } from '@angular/core';

import { map, Observable } from 'rxjs';
import { API_META, BeehiveApiService } from '../beehive-api.service';
import { Staff } from './staff.model';
import { ApiQueryParams } from '../api.operators';
import { Application } from '../application/application.model';
import { ApiResponse } from '../api.interfaces';
import { StaffSkill } from '../skillz/staff-skillz/staff-skillz.model';
import { Activity } from '../activity/activity.model';
import { Document } from '../document/document.model';
import { DocumentCheck } from '../document-check/document-check.model';
import { DocumentType } from '../document/lib/document.interfaces';
import { castDataArrayToEntityArray } from '../cast-data-to-entity';

@Injectable({ providedIn: 'root' })
export class StaffService extends BeehiveApiService<Staff> {
  protected readonly entityType = Staff;
  protected readonly endpoint = 'staff';

  public getMyApplications(
    id: string,
    rawParams?: ApiQueryParams<Staff>,
  ): Observable<Application[]> {
    const params = this.parseQuery(rawParams);
    const url = this.getUrl(
      encodeURIComponent(id),
      encodeURIComponent('applications'),
    );

    return this.http.get<ApiResponse<Application[]>>(url, params).pipe(
      map((response) => {
        const data = castDataArrayToEntityArray(Application, response.data);
        Reflect.defineMetadata(API_META, response.meta, data);
        return data;
      }),
    );
  }

  getStaffDocuments(staff: Staff) {
    return this.getMany(
      [encodeURIComponent(staff.id), 'documents'],
      undefined,
      Document,
    );
  }

  getExpenseNotes(staff: Staff) {
    return this.getMany(
      [encodeURIComponent(staff.id), 'documentChecks'],
      undefined,
      DocumentCheck,
    ).pipe(
      map((checks) => {
        return checks.filter(
          (check) => check.document?.type == DocumentType.ExpenseNote,
        );
      }),
    );
  }

  public getSkills(
    staff: Staff,
    rawParams?: ApiQueryParams<StaffSkill>,
  ): Observable<StaffSkill[]> {
    return this.getMany(
      [encodeURIComponent(staff.id), 'skills'],
      rawParams,
      StaffSkill,
    );
  }

  getActivities(staff: Staff): Observable<Activity[]> {
    return this.getMany(
      [encodeURIComponent(staff.id), 'activities'],
      undefined,
      Activity,
    );
  }
}
